import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/context/ToastContext";
import { developmentApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { PrioritySelect } from "@/components/PrioritySelect";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import {
    type DevelopmentRow,
    PRIORITY_CONFIG,
} from "@/pages/Developments/DevelopmentsTable";

export interface ChangePriorityModalProps {
    isOpen: boolean;
    development: DevelopmentRow | null;
    onClose: () => void;
    onSuccess: (updated: DevelopmentRow) => void;
}

export const ChangePriorityModal: React.FC<ChangePriorityModalProps> = ({
    isOpen,
    development,
    onClose,
    onSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess } = useToast();
    const [selectedPriority, setSelectedPriority] = useState<string>("medium");
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (development) {
            setSelectedPriority(development.priority || "medium");
            setError(null);
        }
    }, [development]);

    const ID_TO_TAG: Record<number, string> = {
        1: "low",
        2: "medium",
        3: "high",
        4: "urgent",
    };

    const handleSubmit = async (): Promise<void> => {
        if (!development) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const valNum = Number(selectedPriority);
            const finalTag =
                !isNaN(valNum) && ID_TO_TAG[valNum]
                    ? ID_TO_TAG[valNum]
                    : selectedPriority;

            const res = await developmentApi.update(development.id, {
                priority: finalTag,
            });

            if (res.result) {
                showSuccess(t("developments.modal_priority.success"));
                const updatedRow: DevelopmentRow = {
                    ...development,
                    priority: finalTag,
                };
                onSuccess(updatedRow);
                onClose();
            } else {
                setError(res.message || t("developments.modal_priority.error"));
            }
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("developments.modal_priority.error")
            );
        } finally {
            setLoading(false);
        }
    };

    const currentConfig = development
        ? PRIORITY_CONFIG[development.priority]
        : null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("developments.modal_priority.title")}
            maxWidth="sm"
            onSubmit={handleSubmit}
            submitLabel={t("developments.modal_priority.save_button")}
            submittingLabel={t("developments.modal_priority.saving")}
            isSubmitting={loading}
            submitIcon={<Icon value="check" size={15} />}
        >
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center space-x-2 mb-3">
                    <Icon value="alertCircle" size={16} className="shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {development && (
                <div className="text-center">
                    <Badge value={development.code} />
                    <div className="">{development.title}</div>
                    {/*currentConfig && (
                        <div className="mt-1">                            
                            <span className={`inline-flex items-center ${currentConfig.color}`}>
                                {Array.from({ length: currentConfig.arrows }).map((_, i) => (
                                    <Icon key={i} value="arrowUp" size={20} strokeWidth={3} />
                                ))}
                            </span>            
                        </div>
                    )*/}
                </div>
            )}

            <PrioritySelect
                value={selectedPriority}
                onChange={setSelectedPriority}
                //label={t('developments.modal_priority.field_priority')}
            />
        </Modal>
    );
};
